import { openImageCropper } from "@/dialogs/cropper-dialog";
import Icon from "@components/ui/icon";
import {
	MediaLibPresetEnum,
	openMediaLibDialog,
} from "@editor/modules/media-lib";
import { environment } from "@environment";
import { cn } from "@lib/cn";
import type React from "react";
import { useCallback } from "react";

interface Props {
	value: string;
	onChange: (value: string) => void;
	kind: "image" | "video";
	invalid?: boolean;
}

/**
 * Media cell for a repeatable row.
 *
 * Deliberately mirrors the regular-variable media controls (controls/image.tsx
 * and controls/video.tsx): the same 60x40 dashed preview box, the same icon
 * placeholder, the same checkerboard backdrop behind a chosen image, and — for
 * images — the same crop step after picking. It previously rendered a plain
 * "Add media" text button, which looked nothing like the rest of the dialog.
 */
export const MediaCell = ({
	value,
	onChange,
	kind,
	invalid = false,
}: Props) => {
	const openPicker = useCallback(() => {
		openMediaLibDialog({
			preset:
				kind === "video"
					? MediaLibPresetEnum.OnlyVideos
					: MediaLibPresetEnum.OnlyImages,
			onSelect: (item) => {
				if (kind === "video") {
					onChange(item.mediaFile.url);
					return;
				}
				// Images go through the cropper, same as a regular image variable.
				openImageCropper({
					imageSrc: item.mediaFile.url,
					onCropAndUpload: (mediaFile) => onChange(mediaFile.url),
				});
			},
		});
	}, [kind, onChange]);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === " ") {
				event.preventDefault();
				openPicker();
			}
		},
		[openPicker],
	);

	return (
		<div className="tw-flex tw-flex-row tw-items-center tw-justify-start">
			<div
				className={cn(
					"tw-relative tw-cursor-pointer tw-overflow-hidden tw-transition-[border-style] tw-duration-200 tw-ease-in-out",
					"tw-flex tw-items-center tw-justify-center",
					"tw-h-[40px] tw-w-[60px]",
					"tw-rounded-md tw-border tw-border-dashed tw-border-[#d0cdd8] tw-bg-[rgba(243,242,245,0.5)]",
					"hover:tw-border-solid focus:tw-outline focus:tw-outline-2 focus:tw-outline-offset-2 focus:tw-outline-[#7b17cf33]",
					invalid && "tw-border-[#D43548] tw-bg-[#FAF1F2]",
				)}
				onClick={openPicker}
				tabIndex={0}
				role="button"
				aria-label={value ? "Change media" : "Add media"}
				onKeyDown={handleKeyDown}
			>
				{value && kind === "image" && (
					<>
						<div className="tw-absolute tw-inset-0">
							<img
								src={`${environment.assetsLink}/assets/images/ShareDialog/image-bg.jpg`}
								role="presentation"
								alt=""
								className="tw-h-full tw-w-full tw-object-cover"
							/>
						</div>
						<img
							src={value}
							alt=""
							role="presentation"
							className="tw-relative tw-z-[1] tw-h-full tw-w-full tw-object-contain"
						/>
					</>
				)}
				{value && kind === "video" && (
					// Same preview as the regular video control (controls/video.tsx):
					// first frame, muted, no controls. Without it a picked video looked
					// exactly like an empty cell.
					<video
						muted
						playsInline
						preload="metadata"
						aria-label="Selected video"
						// `src` directly, no `<source type>`: a WebM/MOV pick must not be
						// skipped by a hard-coded MIME type.
						src={`${value}#t=0.1`}
						className="tw-relative tw-z-[1] tw-h-full tw-w-full tw-object-cover"
					/>
				)}
				{(invalid || !value) && (
					<div className="tw-absolute tw-left-0 tw-top-0 tw-flex tw-size-full tw-items-center tw-justify-center">
						<Icon
							icon={kind === "video" ? "ri-video-fill" : "ri-image-2-fill"}
							className={cn(
								"!tw-text-[24px]/none tw-text-blue-400",
								invalid && "tw-text-[#D43548]",
							)}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
